/**
 * Consume the oldest optimistic thinking placeholder at most once per
 * assistant/system message id.
 *
 * This keeps streaming updates and duplicate subscription deliveries from
 * draining additional placeholders when multiple user requests are in flight.
 *
 * @param {Object} params
 * @param {Array<Object>} params.messages
 * @param {Array<string>} params.pendingOptimisticIds
 * @param {Map<string, string>} params.consumedByMessageId
 * @param {string} params.messageId
 * @param {string} [params.role]
 * @returns {{messages:Array<Object>, pendingOptimisticIds:Array<string>, consumedByMessageId:Map<string,string>}}
 */
export function consumeOptimisticThinkingForMessage({
  messages,
  pendingOptimisticIds,
  consumedByMessageId,
  messageId,
  role
}) {
  const safeMessages = Array.isArray(messages) ? messages : [];
  const safePending = Array.isArray(pendingOptimisticIds) ? pendingOptimisticIds : [];
  const safeConsumed = consumedByMessageId instanceof Map ? consumedByMessageId : new Map();
  const normalizedMessageId = String(messageId || '').trim();

  if (!normalizedMessageId) {
    return {
      messages: safeMessages,
      pendingOptimisticIds: safePending,
      consumedByMessageId: safeConsumed
    };
  }

  const normalizedRole = String(role || '').trim().toLowerCase();
  if (normalizedRole && normalizedRole !== 'assistant' && normalizedRole !== 'system') {
    return {
      messages: safeMessages,
      pendingOptimisticIds: safePending,
      consumedByMessageId: safeConsumed
    };
  }

  const previouslyConsumedOptimisticId = safeConsumed.get(normalizedMessageId);
  if (previouslyConsumedOptimisticId) {
    if (!safeMessages.some((message) => message?.id === previouslyConsumedOptimisticId)) {
      return {
        messages: safeMessages,
        pendingOptimisticIds: safePending,
        consumedByMessageId: safeConsumed
      };
    }
    return {
      messages: safeMessages.filter((message) => message?.id !== previouslyConsumedOptimisticId),
      pendingOptimisticIds: safePending,
      consumedByMessageId: safeConsumed
    };
  }

  const nextOptimisticId = safePending[0];
  if (!nextOptimisticId) {
    return {
      messages: safeMessages,
      pendingOptimisticIds: safePending,
      consumedByMessageId: safeConsumed
    };
  }

  const nextConsumed = new Map(safeConsumed);
  nextConsumed.set(normalizedMessageId, nextOptimisticId);
  return {
    messages: safeMessages.filter((message) => message?.id !== nextOptimisticId),
    pendingOptimisticIds: safePending.slice(1),
    consumedByMessageId: nextConsumed
  };
}
